/* eslint-disable prettier/prettier */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Breadcrumbs, Button, Chip, Typography, Divider } from '@mui/material';
import { FileDownload, TableChart, Apartment, Stairs, MeetingRoom, People, NavigateNext, Home } from '@mui/icons-material';
import MainCard from 'ui-component/cards/MainCard';
import axios, { get } from 'axios';
import ReactToPrint from 'react-to-print';
import * as XLSX from 'xlsx';
import TableComponent from './TableComponent';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';

let buidingColumns;
let floorColumns;
let roomColumns;
let studentColumns;

const HostelReports = () => {
    const location = useLocation();
    const printRef = useRef();

    const isDashboard = location.pathname === '/hostel/dashboard';

    buidingColumns = [
        {
            field: 'id',
            headerName: '#',
            width: 80,
            renderCell: (params) => (params.row.buildingName !== 'Total' ? params.api.getRowIndex(params.row.id) + 1 : '')
        },
        {
            field: 'buildingName',
            headerName: 'Building Name',
            width: 300,
            renderCell: (params) => (
                <span style={{ cursor: 'pointer', color: params.row.buildingName === 'Total' ? '#005e9f' : '', fontWeight: 'bold' }}>
                    {params.row.buildingName}
                </span>
            )
        },
        { field: 'capacity', headerName: 'Capacity', width: 120 },
        { field: 'occupied', headerName: 'Occupied', width: 120, valueGetter: (p) => p.row.capacity - p.row.vacancy },
        {
            field: '@',
            headerName: 'In (%)',
            width: 80,
            valueGetter: (p) => (((p.row.capacity - p.row.vacancy) / p.row.capacity) * 100).toFixed(2)
        },
        { field: 'vacancy', headerName: 'Vacant', width: 120 },
        { field: '#', headerName: 'In (%)', width: 120, valueGetter: (p) => ((p.row.vacancy / p.row.capacity) * 100).toFixed(2) }
    ];

    floorColumns = [
        {
            field: 'floor',
            headerName: 'Floor Number',
            width: 160,
            renderCell: ({ row }) => (
                <span style={{ cursor: 'pointer' }}>{row.floor === 1 ? 'Ground Floor' : `${row.floor - 1} Floor`}</span>
            )
        },
        { field: 'capacity', headerName: 'Capacity', width: 120 },
        { field: 'occupied', headerName: 'Occupied', width: 120, valueGetter: (p) => p.row.capacity - p.row.vacancy },
        {
            field: '@',
            headerName: 'In (%)',
            width: 120,
            valueGetter: (p) => (p.row.capacity ? (((p.row.capacity - p.row.vacancy) / p.row.capacity) * 100).toFixed(2) : '0')
        },
        { field: 'vacancy', headerName: 'Vacant', width: 100 },
        {
            field: '#',
            headerName: 'In (%)',
            width: 120,
            valueGetter: (p) => (p.row.capacity ? ((p.row.vacancy / p.row.capacity) * 100).toFixed(2) : '0')
        }
    ];

    roomColumns = [
        { field: 'id', headerName: '#', width: 80, renderCell: (index) => index.api.getRowIndex(index.row.id) + 1 },
        { field: 'roomName', headerName: 'Room Name', width: isDashboard ? 150 : 120 },
        { field: 'capacity', headerName: 'Capacity', width: isDashboard ? 180 : 120 },
        { field: 'occupied', headerName: 'Occupied', width: isDashboard ? 180 : 120, valueGetter: (p) => p.row.capacity - p.row.vacancy },
        { field: 'vacancy', headerName: 'Vacant', width: isDashboard ? 180 : 120, valueGetter: (p) => p.row.vacancy }
    ];

    studentColumns = [
        { field: 'id', headerName: '#', width: 80, renderCell: (index) => index.api.getRowIndex(index.row.id) + 1 },
        {
            field: 'studentName',
            headerName: 'Student Name',
            width: isDashboard ? 170 : 280,
            valueGetter: ({ row }) => row.studentMini.studentName
        },
        {
            field: 'courseType',
            headerName: 'Course Type',
            width: isDashboard ? 170 : 200,
            valueGetter: ({ row }) => row.studentMini.courseType
        },
        { field: 'rollNo', headerName: 'Roll Number', width: isDashboard ? 170 : 200, valueGetter: ({ row }) => row.studentMini.rollNo },
        {
            field: 'enrollmentNo',
            headerName: 'Enrollment Number',
            width: isDashboard ? 170 : 200,
            valueGetter: ({ row }) => row.studentMini.enrollmentNo
        }
    ];

    const [buildingRows, setBuildingRows] = useState([]);
    const [buildingId, setBuildingId] = useState('');
    const [floorList, setFloorList] = useState([]);
    const [roomList, setRoomList] = useState([]);
    const [studentList, setStudentList] = useState([]);
    const [navigationIndex, setNavigationIndex] = useState(0);
    const [breadcrumbs, setBreadcrumbs] = useState([{ label: 'Buildings', index: 0 }]);

    // ─── helpers to get current rows/columns ───────────────────────────────────
    const getCurrentRows = () => {
        if (navigationIndex === 0) return buildingRows;
        if (navigationIndex === 1) return floorList;
        if (navigationIndex === 2) return roomList;
        return studentList;
    };

    const getCurrentColumns = () => {
        if (navigationIndex === 0) return buidingColumns;
        if (navigationIndex === 1) return floorColumns;
        if (navigationIndex === 2) return roomColumns;
        return studentColumns;
    };

    const getCurrentTitle = () => breadcrumbs[breadcrumbs.length - 1]?.label || 'Hostel Report';
    const buildingTitle = 'Hostel Occupancy Report';
    const getReportTitle = () => {
        const selectedHostelName = breadcrumbs[1]?.label || '';
        const selectedFloor = breadcrumbs[2]?.label || '';
        const selectedRoom = breadcrumbs[3]?.label || '';

        if (navigationIndex === 0) return buildingTitle;
        if (navigationIndex === 1) return selectedHostelName ? `${buildingTitle} - ${selectedHostelName}` : buildingTitle;
        if (navigationIndex === 2) return [buildingTitle, selectedHostelName, selectedFloor].filter(Boolean).join(' - ');
        return [buildingTitle, selectedHostelName, selectedFloor, selectedRoom].filter(Boolean).join(' - ');
    };
    const getCurrentPageTitle = () => getReportTitle();

    // ─── resolve a cell value the same way DataGrid does ───────────────────────
    const resolveCellValue = (col, row) => {
        if (col.valueGetter) return col.valueGetter({ row });
        return row[col.field] ?? '';
    };

    // ─── Excel export ───────────────────────────────────────────────────────────
    const handleExcelDownload = () => {
        const rows = getCurrentRows();
        const cols = getCurrentColumns().filter((c) => c.field !== 'id');
        const headers = cols.map((c) => c.headerName);
        const data = rows.map((row) => cols.map((col) => resolveCellValue(col, row)));
        const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, getCurrentTitle());
        XLSX.writeFile(wb, `${getCurrentPageTitle()}.xlsx`);
    };

    // ─── print table rows ───────────────────────────────────────────────────────
    const getPrintRows = () => {
        const rows = getCurrentRows();
        const cols = getCurrentColumns().filter((c) => c.field !== 'id');
        return { cols, rows };
    };

    // ─── API fetches ────────────────────────────────────────────────────────────
    const fetchAllBuildings = async () => {
        try {
            const response = await axios.get(`/v1/hostel/dashboard/buildings/report`, {
                headers: { Authorization: `Bearer ${window.localStorage.getItem('serviceToken')}`, 'Content-Type': 'application/json' }
            });
            const buildingData = response.data.buildingsReport.sort((a, b) => a.buildingName.localeCompare(b.buildingName));
            const totalCapacity = buildingData.reduce((acc, b) => acc + b.capacity, 0);
            const totalOccupied = buildingData.reduce((acc, b) => acc + (b.capacity - b.vacancy), 0);
            const totalVacancy = buildingData.reduce((acc, b) => acc + b.vacancy, 0);
            setBuildingRows([
                ...buildingData,
                { id: 'total', buildingName: 'Total', capacity: totalCapacity, occupied: totalOccupied, vacancy: totalVacancy }
            ]);
        } catch (error) {
            console.error({ error });
        }
    };

    const handleCellClickBuidling = async (params) => {
        if (params.row.buildingName === 'Total') return;
        setBuildingId(params.row.id);
        try {
            const res = await axios.get(`/v1/hostel/dashboard/floors/report?buildingId=${params.row.id}`, {
                headers: { Authorization: `Bearer ${window.localStorage.getItem('serviceToken')}`, 'Content-Type': 'application/json' }
            });
            if (res.status === 200) {
                setFloorList(res.data.floorsReport.filter((o) => o.floor !== 0).map((o) => ({ ...o, id: o.floor })));
                setNavigationIndex(1);
                setBreadcrumbs([
                    { label: 'Buildings', index: 0 },
                    { label: params.row.buildingName, index: 1 }
                ]);
            }
        } catch (error) {
            console.error({ error });
        }
    };

    const handleCellClickFloor = async (params) => {
        try {
            const res = await axios.get(
                `/v1/hostel/dashboard/rooms/report?buildingId=${buildingId}&floor=${params.row.floor}&status=true`,
                {
                    headers: { Authorization: `Bearer ${window.localStorage.getItem('serviceToken')}`, 'Content-Type': 'application/json' }
                }
            );
            setRoomList(res.data.roomsReport.sort((a, b) => Number(a.roomName) - Number(b.roomName)));
            setNavigationIndex(2);
            setBreadcrumbs((prev) => [
                ...prev.slice(0, 2),
                { label: params.row.floor === 1 ? 'Ground Floor' : `Floor ${params.row.floor - 1}`, index: 2 }
            ]);
        } catch (error) {
            console.error({ error });
        }
    };

    const handleCellClickRoom = async (params) => {
        try {
            const res = await axios.get(`/v1/hostel/rooms/students/list?roomId=${params.row.id}`, {
                headers: { Authorization: `Bearer ${window.localStorage.getItem('serviceToken')}`, 'Content-Type': 'application/json' }
            });
            const sorted = _.sortBy(res.data.hostelStudents, (s) => parseInt(s.studentMini.rollNo.split('/').pop(), 10));
            setStudentList(sorted);
            setNavigationIndex(3);
            setBreadcrumbs((prev) => [...prev.slice(0, 3), { label: `Room ${params.row.roomName}`, index: 3 }]);
        } catch (error) {
            console.error({ error });
        }
    };

    const handleBreadcrumbClick = (targetIndex) => {
        setNavigationIndex(targetIndex);
        setBreadcrumbs((prev) => prev.slice(0, targetIndex + 1));
    };

    const crumbIcons = [Home, Apartment, Stairs, MeetingRoom, People];
    const crumbColors = ['default', 'primary', 'secondary', 'warning', 'success'];

    useEffect(() => {
        fetchAllBuildings();
    }, []);

    const { cols: printCols, rows: printRows } = getPrintRows();

    const pageTitle = (
        <Breadcrumbs
            separator={<NavigateNext fontSize="small" sx={{ color: 'text.disabled' }} />}
            aria-label="hostel-navigation"
            sx={{ '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap', alignItems: 'center' } }}
        >
            {breadcrumbs.map((crumb, i) => {
                const isLast = i === breadcrumbs.length - 1;
                const Icon = crumbIcons[i] ?? Home;
                const color = crumbColors[i] ?? 'default';
                return isLast ? (
                    <Chip
                        key={crumb.index}
                        icon={<Icon sx={{ fontSize: '15px !important' }} />}
                        label={crumb.label}
                        color={color}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 13, px: 0.5 }}
                    />
                ) : (
                    <Chip
                        key={crumb.index}
                        icon={<Icon sx={{ fontSize: '15px !important' }} />}
                        label={crumb.label}
                        size="small"
                        variant="outlined"
                        onClick={() => handleBreadcrumbClick(crumb.index)}
                        sx={{ fontWeight: 500, fontSize: 13, cursor: 'pointer', px: 0.5 }}
                    />
                );
            })}
        </Breadcrumbs>
    );

    const pageActions = (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<TableChart />} onClick={handleExcelDownload}>
                Download Excel
            </Button>
            <ReactToPrint
                trigger={() => (
                    <Button variant="contained" size="small" startIcon={<FileDownload />}>
                        Download PDF
                    </Button>
                )}
                content={() => printRef.current}
                pageStyle={`
                    @page { size: A4 landscape; margin: 12mm; }
                    @media print {
                        body { -webkit-print-color-adjust: exact; }
                        table { width: 100%; border-collapse: collapse; font-size: 11px; }
                        th, td { border: 1px solid #ccc; padding: 5px 8px; text-align: left; }
                        th { background-color: #f5f5f5; font-weight: 700; }
                        tr { page-break-inside: avoid; }
                    }
                `}
            />
        </Box>
    );

    return (
        <MainCard title={pageTitle} secondary={pageActions}>

            {/* ── DataGrid table ── */}
            <div style={{ height: 500, width: '100%' }}>
                {navigationIndex === 0 && (
                    <TableComponent rows={buildingRows} columns={buidingColumns} handleRowClick={handleCellClickBuidling} />
                )}
                {navigationIndex === 1 && (
                    <TableComponent
                        rows={floorList}
                        columns={floorColumns}
                        handleRowClick={handleCellClickFloor}
                        getRowId={(row) => row.floor}
                    />
                )}
                {navigationIndex === 2 && <TableComponent rows={roomList} columns={roomColumns} handleRowClick={handleCellClickRoom} />}
                {navigationIndex === 3 && <TableComponent rows={studentList} columns={studentColumns} handleRowClick={() => {}} />}
            </div>

            {/* ── Hidden print content ── */}
            <div style={{ display: 'none' }}>
                <div ref={printRef} style={{ padding: '16px', fontFamily: 'Times New Roman', fontSize: '12px', backgroundColor: '#fff' }}>
                    {/* Header image — same as SemesterMarksPreview */}
                    <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                        <img src="/updated_university_header.png" alt="University Header" style={{ maxWidth: '100%', height: 'auto' }} />
                    </div>

                    <Divider sx={{ mb: 1 }} />

                    {/* Report title */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                        <Typography
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '15px',
                                letterSpacing: '1.5px',
                                px: 2,
                                py: 0.5
                            }}
                        >
                            {getReportTitle()}
                        </Typography>
                    </Box>

                    {/* Breadcrumb path in print */}
                    {/* <Typography sx={{ fontSize: '11px', mb: 1.5, color: '#555' }}>
                        Path: {breadcrumbs.map((b) => b.label).join(' > ')}
                    </Typography> */}

                    {/* Data table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                <th style={{ border: '1px solid #ccc', padding: '5px 8px' }}>#</th>
                                {printCols.map((col) => (
                                    <th key={col.field} style={{ border: '1px solid #ccc', padding: '5px 8px', whiteSpace: 'nowrap' }}>
                                        {col.headerName}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {printRows.map((row, i) => (
                                <tr key={row.id ?? i}>
                                    <td style={{ border: '1px solid #ccc', padding: '5px 8px', textAlign: 'center' }}>{i + 1}</td>
                                    {printCols.map((col) => (
                                        <td key={col.field} style={{ border: '1px solid #ccc', padding: '5px 8px' }}>
                                            {resolveCellValue(col, row)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </MainCard>
    );
};

export default HostelReports;
